import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from "react";

const Courses = (props) => {
  const { user, allCourses, users, setUsers } = props;

  const [availableCourses, setAvailableCourses] = useState(
    allCourses.filter(
      (course) => !user.courses?.some((c) => c.id === course.id),
    ),
  );
  const [courses, setCourses] = useState(user.courses || []);
  const [openCoursesDropdown, setOpenCoursesDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenCoursesDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setCourses(user.courses || []);
  }, [user]);

  const patchClient = async (params) => {
    return await fetch("/users_management/" + user.id + "/courses", {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        user: params,
      }),
    });
  };

  const handleNewCourse = async (courseId) => {
    const response = await patchClient({
      course_ids: [...courses.map((c) => c.id), courseId],
    });

    if (response.ok) {
      const data = await response.json();
      setCourses(data.user.courses);
      setUsers(
        users.map((oldUser) => (oldUser.id == user.id ? data.user : oldUser)),
      );

      setOpenCoursesDropdown(false);
      setSearchTerm(""); // Clear search when course is added
    } else {
      console.log("error");
    }
  };

  const handleDeleteCourse = async (courseId) => {
    const response = await patchClient({
      course_ids: courses.filter((c) => c.id !== courseId).map((c) => c.id),
    });

    if (response.ok) {
      const data = await response.json();
      setCourses(data.user.courses);
      setUsers(
        users.map((oldUser) => (oldUser.id == user.id ? data.user : oldUser)),
      );
      setOpenCoursesDropdown(false);
    } else {
      console.log("error");
    }
  };

  useEffect(() => {
    setAvailableCourses(
      allCourses.filter((course) => !courses.some((c) => c.id === course.id)),
    );
  }, [courses]);

  // Filter available courses based on search term
  const filteredCourses = availableCourses.filter((course) =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleDropdownToggle = () => {
    setOpenCoursesDropdown(!openCoursesDropdown);
    if (!openCoursesDropdown) {
      setSearchTerm(""); // Clear search when opening dropdown
    }
  };

  const handleCertificateDownload = async (course) => {
    try {
      const response = await fetch(
        `/client_admin/certificates/${course.id}?user_id=${user.id}`,
        {
          method: "GET",
          headers: {
            Accept: "application/pdf",
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]')
              .content,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${user.name} ${course.title} certyfikat.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Failed to download certificate");
      }
    } catch (error) {
      console.error("Error downloading certificate:", error);
    }
  };

  const coursesTable = courses.map((course) => {
    return (
      <div key={course.id} className="bg-gray-50 p-4 mb-3">
        <div className="flex flex-col w-full">
          <div className="font-medium text-lg mb-1 underline">
            {course.title}
          </div>
          <div className="flex justify-between items-start gap-4">
            <div className="w-full">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">
                  {course.progress == 100 ? "Ukończone" : "W trakcie"}
                </span>
                <span className="text-xs text-gray-400">
                  {course.progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                <div
                  className="bg-black h-2 rounded-full transition-all duration-300 ease-in-out"
                  style={{ width: `${course.progress}%` }}
                ></div>
              </div>
            </div>

            <div className="text-right text-sm text-gray-600">
              <button
                className="flex w-max items-center gap-2
                  px-6 py-2 bg-white text-primaryYellow border border-primaryYellow text-sm
                  hover:bg-black hover:text-white
                  enabled:cursor-pointer
                  transition-colors
                "
                onClick={() => handleDeleteCourse(course.id)}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3H5m14 0H5"
                  />
                </svg>
                Usuń dostęp
              </button>
            </div>
          </div>

          {course.progress == 100 && (
            <div className="text-center">
              <button
                onClick={() => handleCertificateDownload(course)}
                className="flex w-max items-center gap-2
                   text-primaryYellow text-sm
                  hover:text-black
                  enabled:cursor-pointer
                  transition-colors underline
                "
              >
                Pobierz certyfikat ukończenia
              </button>
            </div>
          )}
        </div>
      </div>
    );
  });

  return (
    <>
      <div className="border-b">
        {availableCourses.length ? (
          <button
            onClick={handleDropdownToggle}
            className="flex justify-between w-full border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium hover:bg-gray-50"
          >
            <span className="text-sm font-medium">
              {availableCourses[0].title}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-light">dodaj</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                />
              </svg>
            </div>
          </button>
        ) : (
          <></>
        )}
      </div>

      <div className="relative">
        {openCoursesDropdown && (
          <div
            className="absolute w-full mt-1 border bg-white shadow-lg z-50"
            ref={dropdownRef}
          >
            {/* Search input */}
            <div className="p-3 border-b border-gray-200">
              <input
                type="text"
                placeholder="Szukaj szkoleń..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Courses list */}
            <div className="max-h-60 overflow-auto">
              {filteredCourses.length > 0 ? (
                filteredCourses.map((course) => (
                  <button
                    key={course.id}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                    onClick={() => handleNewCourse(course.id)}
                  >
                    {course.title}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  Nie znaleziono szkoleń
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-5">{coursesTable}</div>
      </div>
    </>
  );
};

export default Courses;

Courses.propTypes = {
  user: PropTypes.object,
  allCourses: PropTypes.array,
  users: PropTypes.array,
  setUsers: PropTypes.func,
};
